import { Injectable } from '@angular/core';
import { ApiService } from 'app/shared/api.service';
import { BehaviorSubject } from 'rxjs';

/* Interfaces */
export interface Area {
    id: string;
    name: string;
    properties: any;
    created_at: string;
    updated_at: string;
}

export interface Property {
    id: string;
    area_id?: string;
    area?: { id: string; name: string };
    name: string;
    address: string | null;
    province: any;
    regency: any;
    district: any;
    village: any;
    latitude: string;
    longitude: string;
    image: string | null;
}

export interface Floor {
    id: string;
    property_id: string;
    label: string;
    count: number;
}

export interface FacilityCategory {
    id: string;
    name: string;
    count: number;
}

export interface Facility {
    id: string;
    name: string;
    description: string | null;
    image: string | null;
    price: number;
    is_active: boolean;
    category?: {
        id: string;
        name: string;
        count: number;
    };
    category_name?: string;
    facility_category_id?: string;
    property_id: string;
}

export interface ViolationType {
    id?: string;
    name: string;
    description?: string;
    points: number;
    level: number;
    severity: string;
    is_active: boolean;
}

export interface Room {
    id: string;
    grid_no?: number;
    floor_id: string;
    label: string;
    status: string | null;
    eligible_to_delete?: boolean;
    facilities: Facility[];
    property: any;
    room_type?: {
        id: string;
        label: string;
        products?: {
            id: string;
            name: string;
            price: number;
            periode?: string;
            periode_label?: string;
            qty?: number;
            status?: string;
            facility_add_ons?: { id: string; name: string; price: number }[];
        }[];
    } | null;
    doorlock_id?: string | null;
    occupant?: Occupant | null;
    prices?: { id?: string; duration: number; duration_unit: string; price: number }[];
    /** @deprecated use prices[] */ price_daily?: number | null;
    /** @deprecated use prices[] */ price_monthly?: number | null;
    /** @deprecated use prices[] */ price_yearly?: number | null;
    active_contract?: {
        id?: string;
        contract_number?: string | null;
        start_date: string | null;
        end_date: string | null;
        grace_date?: string | null;
        check_out_date?: string | null;
        duration_months?: number | null;
        price_per_month?: number | null;
        unit_duration?: string | null;
        value_duration?: number | null;
        unit_duration_label?: string | null;
        duration_label?: string | null;
        status?: string | null;
        status_label?: string | null;
        price?: number | null;
        total_price?: number | null;
        card_id?: string | null;
        violation?: {
            total_points: number;
            total_violations: number;
        } | null;
    } | null;
    properties?: any;
}

export interface Occupant {
    id: string;
    name: string;
    gender: string;
    email: string | null;
    phone: string | null;
    identity_type: string | null;
    identity_number: string | null;
    identity_image: string | null;
    image: string | null;
    occupation: string | null;
    emergency_contact_name: string | null;
    emergency_contact_phone: string | null;
    address: string | null;
}

export interface RoomMaintenanceExtraCost {
    id: string;
    room_maintenance_id: string;
    description: string;
    amount: number;
    created_at?: string;
    updated_at?: string;
}

export interface RoomMaintenance {
    id: string;
    room_id: string;
    description: string;
    start_date: string;
    end_date: string;
    base_cost: number;
    total_cost?: number;
    is_completed?: boolean;
    extra_costs?: RoomMaintenanceExtraCost[];
    created_at?: string;
    updated_at?: string;
}

export type VoucherType = 'FLAT' | 'PERCENTAGE';

export interface VoucherPayload {
    name: string;
    description?: string;
    type: VoucherType;
    value: number;
    max_discount_amount?: number;
    usage_limit: number | null;
    usage_limit_per_occupant: number;
    start_date: string;
    end_date: string;
    is_active: boolean;
    product_ids: string[];
}

export interface VoucherBulkPayload {
    quantity: number;
    code_prefix: string;
    name: string;
    type: VoucherType;
    value: number;
    max_discount_amount?: number;
    usage_limit_per_occupant: number;
    start_date: string;
    end_date: string;
    product_ids: string[];
}

export type VoucherUpdatePayload = Partial<VoucherPayload>;
/* Service */
@Injectable({
    providedIn: 'root'
})
export class KossayaService {
    /** Global state: currently active area & property */
    private _area = new BehaviorSubject<Area | null>(null);
    private _property = new BehaviorSubject<Property | null>(null);
    private _initialized = false;
    private _initPromise: Promise<void> | null = null;

    area$ = this._area.asObservable();
    property$ = this._property.asObservable();

    get area(): Area | null { return this._area.value; }
    get property(): Property | null { return this._property.value; }
    get isInitialized(): boolean { return this._initialized; }

    constructor(private api: ApiService) { }

    /**
     * Initialize: load area[0] Ã¢â€ â€™ property[0] after login.
     * Called once from LayoutComponent.
     * Returns the same promise if already in progress.
     */
    initialize(): Promise<void> {
        if (this._initPromise) return this._initPromise;

        this._initPromise = this._doInit();
        return this._initPromise;
    }

    /** Wait until initialization is complete (safe to call from any child page) */
    async waitForInit(): Promise<void> {
        if (!this._initPromise) {
            this.initialize();
        }
        await this._initPromise;
    }

    private async _doInit(): Promise<void> {
        try {
            // 1. Get first area
            const areas = await this.getAreas();
            if (areas.length > 0) {
                this._area.next(areas[0]);
            }

            // 2. Get first property of this area
            if (this.area) {
                const properties = await this.getPropertiesByArea(this.area.id);
                if (properties.length > 0) {
                    this._property.next(properties[0]);
                }
            }

            this._initialized = true;
        } catch (error) {
            console.error('[KosSaya] Failed to initialize:', error);
        }
    }

    /** Get all areas for current tenant */
    async getAreas(): Promise<Area[]> {
        const res = await this.api.get('api/v1/tenant/manage/area/list?paginate=false');
        return res?.data?.list || res?.list || [];
    }

    /** Get all properties for current tenant */
    async getProperties(): Promise<Property[]> {
        const res = await this.api.get('api/v1/tenant/manage/property/list?paginate=false');
        return res?.data?.list || res?.list || [];
    }

    /** Get properties filtered by area_id (client-side) */
    async getPropertiesByArea(areaId: string): Promise<Property[]> {
        const all = await this.getProperties();
        return all.filter(p => (p.area_id === areaId) || (p.area?.id === areaId));
    }

    /** Get floors for a property (defaults to current property) */
    async getFloors(propertyId?: string): Promise<Floor[]> {
        const pid = propertyId || this.property?.id;
        if (!pid) return [];
        const res = await this.api.get(`api/v1/tenant/manage/floor/list?property_id=${pid}&paginate=false`);
        return res?.data?.list || res?.list || [];
    }

    /** Get rooms for a floor, sorted by grid_no, with optional occupant search */
    async getRooms(floorId: string, searchOccupant?: string, isRentRoom?: number): Promise<Room[]> {
        let url = `api/v1/tenant/manage/room/list?floor_id=${floorId}&paginate=0&per_page=100`;
        if (searchOccupant && searchOccupant.trim().length > 0) {
            url += `&search_occupant=${encodeURIComponent(searchOccupant.trim())}`;
        }
        if (isRentRoom !== undefined) {
            url += `&is_rent_room=${isRentRoom}`;
        }
        const res = await this.api.get(url);
        const list: Room[] = res?.data?.list || res?.list || [];
        // Sort by grid_no ascending (server-side ordering)
        return list.sort((a, b) => (a.grid_no ?? 9999) - (b.grid_no ?? 9999));
    }

    /** Get all rooms for current property */
    async getAllRooms(isRentRoom?: number, withOccupant = false): Promise<Room[]> {
        let url = `api/v1/tenant/manage/room/list?paginate=0&per_page=100`;
        if (isRentRoom !== undefined) {
            url += `&is_rent_room=${isRentRoom}`;
        }
        if (withOccupant) {
            url += '&with_occupant=true';
        }
        const res = await this.api.get(url);
        const list: Room[] = res?.data?.list || res?.list || [];
        return list;
    }

    /** Get rooms with contracts expiring soon (default: soon_to_expire=10, with_occupant=true) */
    async getExpiringSoonRooms(days: number = 10): Promise<any[]> {
        const url = `api/v1/tenant/manage/room/list?soon_to_expire=${days}&with_occupant=true&paginate=0`;
        const res = await this.api.get(url);
        return res?.data?.list || res?.list || res?.data || [];
    }

    /** Update a room's grid position */
    async updateRoomGridNo(roomId: string, gridNo: number): Promise<any> {
        return this.api.post(`api/v1/tenant/manage/room/${roomId}/update-grid`, { grid_no: gridNo });
    }

    /** Build a rental income report URL using the active property and date range. */
    private buildRentalIncomeReportUrl(endpoint: string, startDate: string, endDate: string): string {
        const params: string[] = [];
        if (startDate) params.push(`start_date=${encodeURIComponent(startDate)}`);
        if (endDate) params.push(`end_date=${encodeURIComponent(endDate)}`);
        const pid = this.property?.id;
        if (pid) params.push(`property_id=${encodeURIComponent(pid)}`);
        return params.length ? `${endpoint}?${params.join('&')}` : endpoint;
    }

    /** Get rental income report grouped by rental period. */
    async getRentalIncomeReport(startDate: string, endDate: string): Promise<any> {
        const res = await this.api.get(this.buildRentalIncomeReportUrl(
            'api/v1/tenant/manage/report/rental-income', startDate, endDate
        ));
        return res?.data || null;
    }

    /** Get rental income report grouped by room type. */
    async getRentalIncomeByTypeReport(startDate: string, endDate: string): Promise<any> {
        const res = await this.api.get(this.buildRentalIncomeReportUrl(
            'api/v1/tenant/manage/report/rental-income-by-type', startDate, endDate
        ));
        return res?.data || null;
    }

    async getRoomStats(): Promise<any> {
        const params = new URLSearchParams({ is_rent_room: '1' });
        const pid = this.property?.id;
        if (pid) {
            params.set('property_id', pid);
        }
        const url = `api/v1/tenant/manage/room/stats?${params.toString()}`;
        const res = await this.api.get(url);
        return res?.data || null;
    }

    /** Get Audit Log List with filters */
    async getAuditList(filters?: {
        user_id?: string;
        event?: string;
        start_date?: string;
        end_date?: string;
        search?: string;
        page?: number;
        per_page?: number;
        paginate?: boolean | string;
    }): Promise<any> {
        const queryParams = new URLSearchParams();
        const pid = this.property?.id;
        if (pid) {
            queryParams.set('property_id', pid);
        }
        if (filters) {
            if (filters.user_id) queryParams.set('user_id', filters.user_id);
            if (filters.event) queryParams.set('event', filters.event);
            if (filters.start_date) queryParams.set('start_date', filters.start_date);
            if (filters.end_date) queryParams.set('end_date', filters.end_date);
            if (filters.search) queryParams.set('search', filters.search);
            if (filters.page) queryParams.set('page', filters.page.toString());
            if (filters.per_page) queryParams.set('per_page', filters.per_page.toString());
            if (filters.paginate !== undefined) {
                queryParams.set('paginate', filters.paginate.toString());
            }
        }
        const queryString = queryParams.toString();
        const url = `api/v1/tenant/manage/audit/list${queryString ? '?' + queryString : ''}`;
        const res = await this.api.get(url);
        const data = res?.data || res;
        // If returning for simple list callers (like dashboard)
        if (Array.isArray(data?.list)) {
            return data.list;
        }
        if (Array.isArray(data?.data)) {
            return data;
        }
        if (Array.isArray(data)) {
            return data;
        }
        return data || [];
    }

    /** Get Audit Log Stats */
    async getAuditStats(filters?: { start_date?: string; end_date?: string }): Promise<any> {
        const queryParams = new URLSearchParams();
        const pid = this.property?.id;
        if (pid) {
            queryParams.set('property_id', pid);
        }
        if (filters?.start_date) queryParams.set('start_date', filters.start_date);
        if (filters?.end_date) queryParams.set('end_date', filters.end_date);

        const queryString = queryParams.toString();
        const url = `api/v1/tenant/manage/audit/stats${queryString ? '?' + queryString : ''}`;
        const res = await this.api.get(url);
        return res?.data || res || null;
    }

    /** Create a new floor */
    async createFloor(label: string): Promise<any> {
        const pid = this.property?.id;
        if (!pid) throw new Error('No property selected');
        return this.api.post('api/v1/tenant/manage/floor', {
            property_id: pid,
            label
        });
    }

    /** Update a floor label */
    async updateFloor(floorId: string, label: string): Promise<any> {
        return this.api.put(`api/v1/tenant/manage/floor/${floorId}`, { label });
    }

    /** Delete a floor */
    async deleteFloor(floorId: string): Promise<any> {
        return this.api.delete(`api/v1/tenant/manage/floor`, floorId);
    }

    /** Delete a room */
    async deleteRoom(roomId: string): Promise<any> {
        return this.api.delete(`api/v1/tenant/manage/room`, roomId);
    }

    /** Get room detail by ID */
    async getRoomDetail(roomId: string): Promise<Room | null> {
        const res = await this.api.get(`api/v1/tenant/manage/room/${roomId}`);
        return res?.data || null;
    }

    /** Get room report by ID */
    async getRoomReport(roomId: string): Promise<any> {
        const res = await this.api.get(`api/v1/tenant/manage/report/room/${roomId}`);
        return res?.data || null;
    }

    /** Keep a room until a specific date */
    async keepRoom(roomId: string, keepUntilDate: string): Promise<any> {
        return this.api.post(`api/v1/tenant/manage/room/${roomId}/keep`, {
            keep_until_date: keepUntilDate
        });
    }

    /** Unkeep a room */
    async unkeepRoom(roomId: string): Promise<any> {
        return this.api.post(`api/v1/tenant/manage/room/${roomId}/unkeep`, {});
    }

    /** Get all bookings */
    async getBookings(search?: string): Promise<any[]> {
        let url = `api/v1/tenant/manage/bookings`;
        if (search) {
            url += `?search=${encodeURIComponent(search)}`;
        }
        const res = await this.api.get(url);
        return res?.data?.list || res?.list || res?.data || [];
    }

    /** Get booking detail by ID */
    async getBookingDetail(bookingId: string): Promise<any> {
        const res = await this.api.get(`api/v1/tenant/manage/bookings/${bookingId}`);
        return res?.data || null;
    }

    /** Create a new booking */
    async createBooking(data: any): Promise<any> {
        return this.api.post(`api/v1/tenant/manage/bookings`, data);
    }
    /** Cancel a booking */
    async cancelBooking(bookingId: string, cancellationReason?: string): Promise<any> {
        return this.api.post(`api/v1/tenant/manage/bookings/${bookingId}/cancel`, {
            cancellation_reason: cancellationReason || null
        });
    }


    /** Create a new room */
    async createRoom(
        floorId: string,
        label: string,
        productId?: string,
        doorlockId?: string,
        properties?: { number_electricity_token?: string },
        isRentRoom?: number,
        roomTypeId?: string
    ): Promise<any> {
        const body: any = {
            floor_id: floorId,
            label,
        };
        if (roomTypeId) {
            body.room_type_id = roomTypeId;
        }
        if (productId) {
            body.product_id = productId;
        }
        if (doorlockId) {
            body.doorlock_id = doorlockId;
        }
        if (properties) {
            body.properties = properties;
        }
        if (isRentRoom !== undefined) {
            body.is_rent_room = isRentRoom;
        }
        return this.api.post('api/v1/tenant/manage/room', body);
    }

    /** Update a room */
    async updateRoom(roomId: string, data: {
        label?: string;
        floor_id?: string;
        room_type_id?: string | null;
        product_id?: string | null;
        doorlock_id?: string | null;
        properties?: { number_electricity_token?: string };
        is_rent_room?: number;
    }): Promise<any> {
        return this.api.put(`api/v1/tenant/manage/room/${roomId}`, data);
    }

    /** Mark room as available (empty) */
    async setRoomAvailable(roomId: string): Promise<any> {
        return this.api.post(`api/v1/tenant/manage/room/${roomId}/set-available`, {});
    }

    /** Set room as inactive (soft-delete for rooms with history) */
    async setRoomInactive(roomId: string): Promise<any> {
        return this.api.post(`api/v1/tenant/manage/room/${roomId}/set-inactive`, {});
    }

    /** Set room as active (revert from soft-delete) */
    async setRoomActive(roomId: string): Promise<any> {
        return this.api.post(`api/v1/tenant/manage/room/${roomId}/set-available`, {});
    }

    /** Remote unlock a smart doorlock */
    async unlockRoom(roomId: string): Promise<any> {
        return this.api.post(`api/v1/tenant/manage/room/${roomId}/unlock`, {});
    }

    /** Get room opening logs */
    async getRoomUnlockLogs(roomId: string): Promise<any[]> {
        const res = await this.api.get(`api/v1/tenant/manage/room/${roomId}/logs?paginate=false&page=1&per_page=10`);
        return res?.data?.list || res?.list || [];
    }

    /** Get facility add-ons list */
    async getFacilities(params?: { page?: number; per_page?: number; search?: string; facility_category_id?: string }): Promise<any> {
        let url = 'api/v1/tenant/manage/facility-add-on/list';
        const queryParts: string[] = [];

        if (params?.page) queryParts.push(`page=${params.page}`);
        if (params?.per_page) queryParts.push(`per_page=${params.per_page}`);
        if (params?.search) queryParts.push(`search=${encodeURIComponent(params.search)}`);
        if (params?.facility_category_id) queryParts.push(`facility_category_id=${params.facility_category_id}`);

        if (queryParts.length > 0) {
            url += '?' + queryParts.join('&');
        } else {
            url += '?paginate=false';
        }

        const res = await this.api.get(url);
        if (params?.page) {
            return res?.data || res;
        }
        return res?.data?.list || res?.list || [];
    }

    /** Delete a facility add-on */
    async deleteFacility(id: string): Promise<any> {
        return this.api.delete('api/v1/tenant/manage/facility-add-on', id);
    }

    // Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬ Facility Category CRUD Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬

    /** Get all facility categories */
    async getFacilityCategories(): Promise<FacilityCategory[]> {
        const res = await this.api.get('api/v1/tenant/manage/facility-category/list?paginate=false');
        return res?.data?.list || res?.list || [];
    }

    /** Create a new facility category */
    async createFacilityCategory(name: string): Promise<any> {
        return this.api.post('api/v1/tenant/manage/facility-category', { name });
    }

    /** Update a facility category */
    async updateFacilityCategory(id: string, name: string): Promise<any> {
        return this.api.put(`api/v1/tenant/manage/facility-category/${id}`, { name });
    }

    // Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬ Facility Add-on CRUD Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬

    /** Create a new facility add-on */
    async createFacility(data: {
        name: string;
        description?: string;
        price: number;
        is_active: boolean;
        facility_category_id: string;
    }): Promise<any> {
        const pid = this.property?.id;
        if (!pid) throw new Error('No property selected');
        return this.api.post('api/v1/tenant/manage/facility-add-on', {
            ...data,
            property_id: pid
        });
    }

    /** Update a facility add-on */
    async updateFacility(id: string, data: {
        name: string;
        description?: string;
        price: number;
        is_active: boolean;
        facility_category_id: string;
    }): Promise<any> {
        const pid = this.property?.id;
        if (!pid) throw new Error('No property selected');
        return this.api.put(`api/v1/tenant/manage/facility-add-on/${id}`, {
            ...data,
            property_id: pid
        });
    }

    // Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬ Occupant & Rental Contract Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬

    /** Get all occupants */
    async getOccupants(hasActiveContract?: boolean): Promise<Occupant[]> {
        let url = 'api/v1/tenant/manage/occupant/list?paginate=false';
        if (hasActiveContract !== undefined) {
            url += `&has_active_contract=${hasActiveContract}`;
        }
        const res = await this.api.get(url);
        return res?.data?.list || res?.list || [];
    }

    /** Delete an occupant */
    async deleteOccupant(occupantId: string): Promise<any> {
        return this.api.delete('api/v1/tenant/manage/occupant', occupantId);
    }

    /** Create a new occupant */
    async createOccupant(data: {
        name: string;
        gender: string;
        email?: string;
        phone?: string;
        identity_type?: string;
        identity_number?: string;
        occupation?: string;
        emergency_contact_name?: string;
        emergency_contact_phone?: string;
        address?: string;
    }, image?: File, identityImage?: File): Promise<any> {
        const fd = new FormData();
        Object.keys(data).forEach(key => {
            const val = (data as any)[key];
            if (val !== undefined && val !== null && val !== '') {
                fd.append(key, val);
            }
        });
        if (image) {
            fd.append('image', image, image.name);
        }
        if (identityImage) {
            fd.append('identity_image', identityImage, identityImage.name);
        }
        return this.api.postAuto('api/v1/tenant/manage/occupant', fd);
    }

    /** Update an existing occupant */
    async updateOccupant(occupantId: string, data: {
        name: string;
        gender: string;
        email?: string;
        phone?: string;
        identity_type?: string;
        identity_number?: string;
        occupation?: string;
        emergency_contact_name?: string;
        emergency_contact_phone?: string;
        address?: string;
    }, image?: File, identityImage?: File): Promise<any> {
        const fd = new FormData();
        Object.keys(data).forEach(key => {
            const val = (data as any)[key];
            if (val !== undefined && val !== null && val !== '') {
                fd.append(key, val);
            }
        });
        if (image) {
            fd.append('image', image, image.name);
        }
        if (identityImage) {
            fd.append('identity_image', identityImage, identityImage.name);
        }
        return this.api.postAuto(`api/v1/tenant/manage/occupant/${occupantId}`, fd);
    }

    /** Get rental contracts */
    async getRentalContracts(isActive?: boolean): Promise<any[]> {
        let url = 'api/v1/tenant/manage/rental-contract/list?paginate=false';
        if (isActive !== undefined) {
            url += `&is_active=${isActive}`;
        }
        const res = await this.api.get(url);
        return res?.data?.list || res?.list || [];
    }

    /** Get room types that have an active product for a rental period. */
    async getRoomTypesByPeriod(period: string): Promise<any[]> {
        const res = await this.api.get(`api/v1/tenant/manage/room-type/periode/${period}`);
        return res?.data?.list || res?.list || [];
    }

    /** Get rooms belonging to a room type. */
    async getRoomsByRoomType(roomTypeId: string): Promise<any[]> {
        const res = await this.api.get(`api/v1/tenant/manage/room/room-type/${roomTypeId}/rooms`);
        return Array.isArray(res?.data) ? res.data : res?.data?.list || res?.list || [];
    }

    /** Get rooms belonging to a product. */
    async getRoomsByProduct(productId: string): Promise<any[]> {
        const res = await this.api.get(`api/v1/tenant/manage/room/product/${productId}/rooms`);
        return Array.isArray(res?.data) ? res.data : res?.data?.list || res?.list || [];
    }

    /** Get payment methods */
    async getPaymentMethods(): Promise<any[]> {
        const res = await this.api.get('api/v1/tenant/manage/payment-method/list?paginate=false');
        return res?.data?.list || res?.data || res?.list || [];
    }

    /** Validate a voucher against the selected rental product. */
    async validateVoucher(code: string, productId: string, qty?: number): Promise<any> {
        const payload: any = {
            code,
            product_id: productId
        };
        if (qty !== undefined && qty !== null) {
            payload.qty = qty;
        }
        return this.api.post('api/v1/tenant/manage/voucher/validate', payload);
    }

    /** Build FormData for rental contract or booking */
    private buildRentalContractFormData(data: {
        room_id: string;
        product_id?: string;
        room_type_id?: string;
        dp_percentage?: number;
        dp_due_date?: string;
        payment_method_id?: string;
        occupant_id?: string;
        occupant?: Record<string, any>;
        start_date: string;
        end_date?: string;
        grace_date?: string;
        card_id?: string;
        voucher_code?: string;
        booking_id?: string;
        is_pelunasan?: number | string;
        payment_notes?: string;
    }, image?: File, identityImage?: File, paymentProof?: File): FormData {
        const fd = new FormData();
        fd.append('room_id', data.room_id);
        if (data.product_id) fd.append('product_id', data.product_id);
        if (data.room_type_id) fd.append('room_type_id', data.room_type_id);
        if (data.dp_percentage !== undefined && data.dp_percentage !== null) fd.append('dp_percentage', String(data.dp_percentage));
        if (data.dp_due_date) fd.append('dp_due_date', data.dp_due_date);
        if (data.payment_method_id) fd.append('payment_method_id', data.payment_method_id);
        fd.append('start_date', data.start_date);
        if (data.end_date) fd.append('end_date', data.end_date);

        if (data.grace_date) fd.append('grace_date', data.grace_date);
        if (data.occupant_id) fd.append('occupant_id', data.occupant_id);
        if (data.card_id) fd.append('card_id', data.card_id);
        if (data.voucher_code) fd.append('voucher_code', data.voucher_code);

        if (data.booking_id) fd.append('booking_id', data.booking_id);
        if (data.is_pelunasan !== undefined) fd.append('is_pelunasan', String(data.is_pelunasan));
        if (data.payment_notes) fd.append('payment_notes', data.payment_notes);

        if (data.occupant) {
            Object.keys(data.occupant).forEach(key => {
                if (key === 'print_custome_card' || key === 'create_user_occupant') return;
                const val = data.occupant![key];
                if (val !== undefined && val !== null && val !== '') {
                    fd.append(`occupant[${key}]`, val);
                }
            });
            const printCard = data.occupant.print_custome_card !== undefined ? String(data.occupant.print_custome_card) : 'true';
            fd.append('occupant[print_custome_card]', printCard);

            const createUser = data.occupant.create_user_occupant !== undefined ? String(data.occupant.create_user_occupant) : 'true';
            fd.append('occupant[create_user_occupant]', createUser);
        } else {
            fd.append('create_user_occupant', 'false');
            fd.append('print_custome_card', 'false');
        }

        if (image) fd.append('occupant[image]', image, image.name);
        if (identityImage) fd.append('occupant[identity_image]', identityImage, identityImage.name);
        if (paymentProof) fd.append('payment_proof', paymentProof, paymentProof.name);

        return fd;
    }

    /** Create a standard rental contract for a selected room. */
    async createRentalContract(data: any, image?: File, identityImage?: File, paymentProof?: File): Promise<any> {
        const fd = this.buildRentalContractFormData(data, image, identityImage, paymentProof);
        return this.api.postAuto('api/v1/tenant/manage/rental-contract', fd);
    }

    /** Create a rental booking for a selected room. */
    async createRentalBooking(data: any, image?: File, identityImage?: File): Promise<any> {
        const fd = this.buildRentalContractFormData(data, image, identityImage);
        return this.api.postAuto('api/v1/tenant/manage/rental-contract/booking', fd);
    }

    /** Check in booking rental contract */
    async checkInBookingContract(contractId: string, startDate: string): Promise<any> {
        return this.api.post(`api/v1/tenant/manage/rental-contract/${contractId}/check-in`, {
            start_date: startDate
        });
    }

    /** Update rental contract payment status (e.g. pelunasan / PAID) */
    async updateRentalContractPaymentStatus(rentalContractId: string, paymentStatus: string = 'PAID'): Promise<any> {
        return this.api.put(`api/v1/tenant/manage/rental-contract/${rentalContractId}`, {
            payment_status: paymentStatus
        });
    }

    /** Assign access cards to a room */
    async assignCardsToRoom(roomId: string, cardNumbers: string[]): Promise<any> {
        return this.api.post(`api/v1/tenant/manage/room/${roomId}/cards/assign`, {
            card_numbers: cardNumbers
        });
    }

    /** Assign access cards to ALL gates */
    async assignCardsToGates(cardNumbers: string[]): Promise<any> {
        return this.api.post('api/v1/tenant/manage/room/gates/assign-card', {
            card_numbers: cardNumbers
        });
    }

    /** Assign ONE master card to MULTIPLE rooms (bulk) */
    async assignMasterCardToRooms(cardNumber: string, roomIds: string[]): Promise<any> {
        return this.api.post('api/v1/tenant/manage/room/cards', {
            card_number: cardNumber,
            room_ids: roomIds
        });
    }

    /** Get gate device cards for a room */
    async getGateCards(roomId: string): Promise<any[]> {
        const res = await this.api.get(`api/v1/tenant/manage/room/${roomId}/cards/gate-devices`);
        return res?.data || [];
    }

    /** Unassign access cards from gates */
    async unassignCardsFromGates(cardNumbers: string[]): Promise<any> {
        return this.api.post('api/v1/tenant/manage/room/gates/unassign-card', {
            card_numbers: cardNumbers
        });
    }

    /** Unassign access cards from a room */
    async unassignCardsFromRoom(roomId: string, cardIds: string[]): Promise<any> {
        return this.api.post(`api/v1/tenant/manage/room/${roomId}/cards/unassign`, {
            card_ids: cardIds
        });
    }

    /** Get registered cards for a room */
    async getRoomCards(roomId: string): Promise<any[]> {
        const res = await this.api.get(`api/v1/tenant/manage/room/${roomId}/cards/list`);
        return res?.data || [];
    }

    /** Extend rental contract */
    async extendRentalContract(contractId: string, data: any, paymentProof?: File): Promise<any> {
        let payload: any = data;
        if (paymentProof) {
            payload = new FormData();
            for (const key in data) {
                if (data[key] !== undefined && data[key] !== null) {
                    payload.append(key, data[key]);
                }
            }
            payload.append('payment_proof', paymentProof);
            return this.api.postAuto(`api/v1/tenant/manage/rental-contract/${contractId}/extend`, payload);
        }
        return this.api.post(`api/v1/tenant/manage/rental-contract/${contractId}/extend`, data);
    }

    /** Get rental contract extension details */
    async getContractExtensionList(contractId: string): Promise<any> {
        const res = await this.api.get(`api/v1/tenant/manage/rental-contract/${contractId}/extension-list`);
        return res?.data || null;
    }

    /** Terminate rental contract */
    async terminateRentalContract(contractId: string, data: { status: string; grace_date: string }): Promise<any> {
        return this.api.post(`api/v1/tenant/manage/rental-contract/${contractId}/terminate`, data);
    }

    /** Move room (pindah kamar) for a rental contract */
    async moveRoom(contractId: string, data: { new_room_id: string; move_date: string; reason: string }): Promise<any> {
        return this.api.post(`api/v1/tenant/manage/rental-contract/${contractId}/move-room`, data);
    }

    /** Update rental contract dates */
    async updateRentalContract(contractId: string, data: { start_date: string; end_date: string }): Promise<any> {
        return this.api.put(`api/v1/tenant/manage/rental-contract/${contractId}`, data);
    }

    /** OCR KTP: extract identity data from KTP image */
    async ocrKtp(file: File): Promise<any> {
        const fd = new FormData();
        fd.append('file', file, file.name);
        return this.api.postAuto('api/v1/tenant/manage/tools/ocr/ktp', fd);
    }

    // Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬ Administrator: Tenant Management Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬



    // Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬ Master Data: Manajemen Pelanggaran Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬
    async getViolationTypes(): Promise<ViolationType[]> {
        const res = await this.api.get('api/v1/tenant/manage/violations/types');
        return res?.data?.list || res?.data || res?.list || [];
    }

    async createViolationType(data: Partial<ViolationType>): Promise<any> {
        return this.api.post('api/v1/tenant/manage/violations/types', data);
    }

    async updateViolationType(id: string, data: Partial<ViolationType>): Promise<any> {
        return this.api.put(`api/v1/tenant/manage/violations/types/${id}`, data);
    }

    async deleteViolationType(id: string): Promise<any> {
        return this.api.delete('api/v1/tenant/manage/violations/types', id);
    }

    // Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬ Pencatatan Pelanggaran Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬
    async getViolations(params: { contract_id?: string; occupant_id?: string }): Promise<any[]> {
        const queryParts: string[] = [];
        if (params.contract_id) queryParts.push(`contract_id=${params.contract_id}`);
        if (params.occupant_id) queryParts.push(`occupant_id=${params.occupant_id}`);
        const query = queryParts.length > 0 ? '?' + queryParts.join('&') : '';
        const res = await this.api.get(`api/v1/tenant/manage/violations${query}`);
        return res?.data?.list || res?.data || res?.list || [];
    }

    async createViolation(data: { contract_id: string; violation_type_id: string; note?: string; occurred_at?: string }): Promise<any> {
        return this.api.post('api/v1/tenant/manage/violations', data);
    }

    async deleteViolation(id: string): Promise<any> {
        return this.api.delete('api/v1/tenant/manage/violations', id);
    }

    // Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬ Maintenance Room Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬
    async getMaintenanceHistory(roomId: string): Promise<RoomMaintenance[]> {
        const res = await this.api.get(`api/v1/tenant/manage/room/${roomId}/maintenance/history`);
        return res?.data?.list || res?.data || res?.list || [];
    }

    async createMaintenance(roomId: string, data: { description: string; start_date: string; end_date: string; cost: number }): Promise<any> {
        return this.api.post(`api/v1/tenant/manage/room/${roomId}/maintenance`, data);
    }

    async completeMaintenance(maintenanceId: string): Promise<any> {
        return this.api.post(`api/v1/tenant/manage/room/maintenance/${maintenanceId}/complete`, {});
    }

    async updateMaintenance(maintenanceId: string, data: { description?: string; base_cost?: number; start_date?: string; end_date?: string }): Promise<any> {
        return this.api.put(`api/v1/tenant/manage/room/maintenance/${maintenanceId}`, data);
    }

    async deleteMaintenance(maintenanceId: string): Promise<any> {
        return this.api.delete(`api/v1/tenant/manage/room/maintenance`, maintenanceId);
    }

    async createMaintenanceExtraCost(maintenanceId: string, data: { description: string; amount: number }): Promise<any> {
        return this.api.post(`api/v1/tenant/manage/room/maintenance/${maintenanceId}/extra-cost`, data);
    }

    // Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬ Complaints (Pengaduan) Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬
    async getComplaints(params: { rental_contract_id?: string; page?: number; per_page?: number; status?: string; search?: string }): Promise<any> {
        const queryParts: string[] = [];
        if (params.rental_contract_id) queryParts.push(`rental_contract_id=${params.rental_contract_id}`);
        if (params.page) queryParts.push(`page=${params.page}`);
        if (params.per_page) queryParts.push(`per_page=${params.per_page}`);
        if (params.status) queryParts.push(`status=${params.status}`);
        if (params.search) queryParts.push(`search=${encodeURIComponent(params.search)}`);

        const query = queryParts.length > 0 ? '?' + queryParts.join('&') : '';
        const res = await this.api.get(`api/v1/tenant/manage/complaints${query}`);

        // Return full response for pagination handling if page is provided
        if (params.page) return res?.data || res;
        return res?.data?.list || res?.data || res?.list || [];
    }

    async getComplaint(id: string): Promise<any> {
        const res = await this.api.get(`api/v1/tenant/manage/complaints/${id}`);
        return res?.data || res;
    }

    async updateComplaint(id: string, data: { status?: string; admin_note?: string }): Promise<any> {
        return this.api.put(`api/v1/tenant/manage/complaints/${id}`, data);
    }

    async createComplaint(data: { rental_contract_id: string; title: string; description: string }): Promise<any> {
        return this.api.post('api/v1/tenant/manage/complaints', data);
    }

    async updateMaintenanceExtraCost(extraCostId: string, data: { description?: string; amount?: number }): Promise<any> {
        return this.api.put(`api/v1/tenant/manage/room/maintenance/extra-cost/${extraCostId}`, data);
    }

    async deleteMaintenanceExtraCost(extraCostId: string): Promise<any> {
        return this.api.delete(`api/v1/tenant/manage/room/maintenance/extra-cost`, extraCostId);
    }

    // Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬ Occupant (Tenant User) Dashboard Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬

    /** Get occupant dashboard data (for logged-in tenant) */
    async getOccupantDashboard(): Promise<any> {
        const res = await this.api.get('api/v1/occupant/dashboard');
        return res?.data || null;
    }

    /** Open room door for occupant */
    async openDoor(roomId: string): Promise<any> {
        return this.api.post('api/v1/occupant/open-door', { room_id: roomId });
    }

    /** Get occupant complaints */
    async getOccupantComplaints(): Promise<any> {
        const res = await this.api.get('api/v1/occupant/complaints');
        return res?.data || [];
    }

    /** Get occupant complaint detail */
    async getOccupantComplaintDetail(id: string): Promise<any> {
        const res = await this.api.get(`api/v1/occupant/complaints/${id}`);
        return res?.data || null;
    }

    /** Create occupant complaint */
    async createOccupantComplaint(data: { title: string; description: string }): Promise<any> {
        return this.api.post('api/v1/occupant/complaints', data);
    }

    /** Get occupant profile */
    async getOccupantProfile(): Promise<any> {
        const res = await this.api.get('api/v1/occupant/my-profile');
        return res?.data || null;
    }

    /** Update occupant PIN */
    async updateOccupantPin(data: { old_pin: string; new_pin: string; new_pin_confirmation: string }): Promise<any> {
        return this.api.post('api/v1/occupant/update-pin', data);
    }

    /** Get occupant maintenance history */
    async getOccupantMaintenanceHistory(): Promise<any> {
        const res = await this.api.get('api/v1/occupant/maintenance-history');
        return res?.data || [];
    }

    /** Get occupant contracts */
    async getOccupantContracts(): Promise<any> {
        const res = await this.api.get('api/v1/occupant/contracts');
        return res?.data || [];
    }

    /** Get occupant contract detail */
    async getOccupantContractDetail(id: string): Promise<any> {
        const res = await this.api.get(`api/v1/occupant/contract/${id}`);
        return res?.data || null;
    }

    /** Get occupant activity logs */
    async getOccupantActivityLogs(): Promise<any> {
        const res = await this.api.get('api/v1/occupant/activity-logs');
        return res?.data || [];
    }
    // ROOM TYPES & PRODUCTS
    async getRoomTypes(): Promise<any> {
        const pid = this.property?.id;
        let url = 'api/v1/tenant/manage/room-type/list?paginate=false';
        if (pid) url += '&property_id=' + pid;
        const res = await this.api.get(url);
        return res?.data?.list || res?.data || res?.list || [];
    }

    private async resolvePropertyId(): Promise<string> {
        await this.waitForInit();

        if (this.property?.id) {
            return this.property.id;
        }

        const properties = await this.getProperties();
        const fallbackProperty = properties[0];
        if (fallbackProperty?.id) {
            this._property.next(fallbackProperty);
            return fallbackProperty.id;
        }

        throw new Error('Property aktif belum tersedia. Silakan pilih atau buat property terlebih dahulu.');
    }

    async saveRoomType(data: any): Promise<any> {
        const propertyId = await this.resolvePropertyId();
        const payload = { ...data, property_id: propertyId };
        if (data.id) {
            return await this.api.put(`api/v1/tenant/manage/room-type/${data.id}`, payload);
        } else {
            return await this.api.post('api/v1/tenant/manage/room-type', payload);
        }
    }

    async createRoomTypeWithProduct(roomType: any, product: any): Promise<{ roomType: any; product: any }> {
        const roomTypeResponse = await this.saveRoomType({ ...roomType });
        const createdRoomType = roomTypeResponse?.data?.data
            || roomTypeResponse?.data?.room_type
            || roomTypeResponse?.data
            || roomTypeResponse?.room_type
            || roomTypeResponse;
        const roomTypeId = createdRoomType?.id || roomTypeResponse?.id;

        if (!roomTypeId) {
            throw new Error('Tipe kamar berhasil dibuat, tetapi ID tipe kamar tidak ditemukan dari response backend.');
        }

        const productResponse = await this.saveProduct({
            ...product,
            room_type_id: roomTypeId
        });
        return { roomType: roomTypeResponse, product: productResponse };
    }

    async deleteRoomType(id: string): Promise<any> {
        const res = await this.api.delete('api/v1/tenant/manage/room-type', id);
        return res;
    }

    async getProducts(roomTypeId?: string): Promise<any> {
        let url = 'api/v1/tenant/manage/product/list?paginate=false';
        const params = [];
        const pid = this.property?.id;
        if (pid) params.push('property_id=' + pid);
        if (roomTypeId) params.push('room_type_id=' + roomTypeId);
        if (params.length > 0) url += '&' + params.join('&');
        const res = await this.api.get(url);
        return res?.data?.list || res?.data || res?.list || [];
    }

    async getVouchers(page: number = 1, perPage: number = 10): Promise<any> {
        return this.api.get(`api/v1/tenant/manage/voucher/list?page=${page}&per_page=${perPage}`);
    }

    async createVoucher(payload: VoucherPayload): Promise<any> {
        return this.api.post('api/v1/tenant/manage/voucher', payload);
    }

    async createBulkVouchers(payload: VoucherBulkPayload): Promise<any> {
        return this.api.post('api/v1/tenant/manage/voucher/bulk', payload);
    }

    async updateVoucher(id: string, payload: VoucherUpdatePayload): Promise<any> {
        return this.api.put(`api/v1/tenant/manage/voucher/${id}`, payload);
    }

    async deleteVoucher(id: string): Promise<any> {
        return this.api.delete('api/v1/tenant/manage/voucher', id);
    }

    async saveProduct(data: any): Promise<any> {
        const propertyId = await this.resolvePropertyId();
        const payload = { ...data, property_id: propertyId };
        if (data.id) {
            return await this.api.put(`api/v1/tenant/manage/product/${data.id}`, payload);
        } else {
            return await this.api.post('api/v1/tenant/manage/product', payload);
        }
    }

    async deleteProduct(id: string): Promise<any> {
        const res = await this.api.delete('api/v1/tenant/manage/product', id);
        return res;
    }

    async getFacilityAddOns(): Promise<any> {
        const pid = this.property?.id;
        let url = 'api/v1/tenant/manage/facility-add-on/list?paginate=false';
        if (pid) url += '&property_id=' + pid;
        const res = await this.api.get(url);
        return res?.data?.list || res?.data || res?.list || [];
    }



    /** Get comprehensive laporan report */
    async getLaporanLengkap(startDate?: string, endDate?: string): Promise<any> {
        let url = 'api/v1/tenant/manage/report/laporan?';
        if (startDate) url += `&start_date=${startDate}`;
        if (endDate) url += `&end_date=${endDate}`;
        const res = await this.api.get(url);
        return res?.data;
    }

    /* =========================================================================
     *  MANAJEMEN AKSES & SISTEM (USER, ROLE, PERMISSION, TENANT)
     * ========================================================================= */

    // --- USER MANAGEMENT ---
    async getUsers(page: number = 1, perPage: number = 10, search: string = ''): Promise<any> {
        let url = `api/v1/manage/user/list?page=${page}&per_page=${perPage}`;
        if (search) url += `&search=${encodeURIComponent(search)}`;
        return this.api.get(url);
    }

    async createUser(payload: any): Promise<any> {
        return this.api.post('api/v1/manage/user', payload);
    }

    async updateUser(id: string, payload: any): Promise<any> {
        return this.api.put(`api/v1/manage/user/${id}`, payload);
    }

    async deleteUser(id: string): Promise<any> {
        return this.api.delete('api/v1/manage/user', id);
    }

    async clearUserRole(id: string): Promise<any> {
        return this.api.get(`api/v1/manage/user/${id}/clear-role`);
    }

    // --- ROLE MANAGEMENT ---
    async getRoles(page: number = 1, perPage: number = 100, search: string = ''): Promise<any> {
        let url = `api/v1/manage/role/list?page=${page}&per_page=${perPage}`;
        if (search) url += `&search=${encodeURIComponent(search)}`;
        return this.api.get(url);
    }

    async createRole(payload: { name: string }): Promise<any> {
        return this.api.post('api/v1/manage/role', payload);
    }

    async updateRole(id: string, payload: { name: string }): Promise<any> {
        return this.api.put(`api/v1/manage/role/${id}`, payload);
    }

    async deleteRole(id: string): Promise<any> {
        return this.api.delete('api/v1/manage/role', id);
    }

    async setRolePermissions(roleId: string, permissions: string[]): Promise<any> {
        return this.api.post(`api/v1/manage/role/${roleId}/permission`, { permissions });
    }

    // --- PERMISSION MANAGEMENT ---
    async getPermissions(page: number = 1, perPage: number = 200, search: string = ''): Promise<any> {
        let url = `api/v1/manage/permission/list?page=${page}&per_page=${perPage}`;
        if (search) url += `&search=${encodeURIComponent(search)}`;
        return this.api.get(url);
    }

    async createPermission(payload: { name: string }): Promise<any> {
        return this.api.post('api/v1/manage/permission', payload);
    }

    async updatePermission(id: string, payload: { name: string }): Promise<any> {
        return this.api.put(`api/v1/manage/permission/${id}`, payload);
    }

    async deletePermission(id: string): Promise<any> {
        return this.api.delete('api/v1/manage/permission', id);
    }

    // --- TENANT MANAGEMENT ---
    async getTenants(page: number = 1, perPage: number = 50, search: string = ''): Promise<any> {
        let url = `api/v1/manage/tenant/list?page=${page}&per_page=${perPage}`;
        if (search) url += `&search=${encodeURIComponent(search)}`;
        return this.api.get(url);
    }

    async getTenantDetail(tenantId: string): Promise<any> {
        const res = await this.api.get(`api/v1/manage/tenant/${tenantId}`);
        return res?.data || null;
    }

    async createTenant(payload: { name: string; code: string }): Promise<any> {
        return this.api.post('api/v1/manage/tenant', payload);
    }

    async updateTenant(id: string, payload: { name: string; code: string; status?: string }): Promise<any> {
        return this.api.put(`api/v1/manage/tenant/${id}`, payload);
    }

    async deleteTenant(id: string): Promise<any> {
        return this.api.delete('api/v1/manage/tenant', id);
    }

    async setTenantDomain(tenantId: string, domain: string): Promise<any> {
        return this.api.post(`api/v1/manage/tenant/${tenantId}/set-domain`, { domain });
    }

    async unsetTenantDomain(tenantId: string, domain: string): Promise<any> {
        return this.api.post(`api/v1/manage/tenant/${tenantId}/unset-domain`, { domain });
    }

    // ==========================================
    // KEEPER TASK MANAGEMENT & ROOM ROTATION
    // ==========================================

    async getTasks(params?: {
        page?: number;
        per_page?: number;
        date?: string;
        start_date?: string;
        end_date?: string;
        status?: string;
        category?: string;
        priority?: string;
        assigned_to_user_id?: string;
        room_id?: string;
        search?: string;
    }): Promise<any> {
        let url = 'api/v1/tenant/manage/tasks';
        const q: string[] = [];
        if (params?.page) q.push(`page=${params.page}`);
        if (params?.per_page) q.push(`per_page=${params.per_page}`);
        if (params?.date) q.push(`date=${params.date}`);
        if (params?.start_date && params?.end_date) {
            q.push(`start_date=${params.start_date}`);
            q.push(`end_date=${params.end_date}`);
        }
        if (params?.status && params.status !== 'all') q.push(`status=${params.status}`);
        if (params?.category && params.category !== 'all') q.push(`category=${params.category}`);
        if (params?.priority && params.priority !== 'all') q.push(`priority=${params.priority}`);
        if (params?.assigned_to_user_id) q.push(`assigned_to_user_id=${params.assigned_to_user_id}`);
        if (params?.room_id) q.push(`room_id=${params.room_id}`);
        if (params?.search) q.push(`search=${encodeURIComponent(params.search)}`);

        if (q.length > 0) url += '?' + q.join('&');
        return this.api.get(url);
    }

    async getTaskDetail(id: string): Promise<any> {
        return this.api.get(`api/v1/tenant/manage/tasks/${id}`);
    }

    async createTask(payload: any): Promise<any> {
        return this.api.post('api/v1/tenant/manage/tasks', payload);
    }

    async updateTask(id: string, payload: any): Promise<any> {
        return this.api.put(`api/v1/tenant/manage/tasks/${id}`, payload);
    }

    async deleteTask(id: string): Promise<any> {
        return this.api.delete('api/v1/tenant/manage/tasks', id);
    }

    async updateTaskChecklist(id: string, checklist: any[], status?: string): Promise<any> {
        return this.api.post(`api/v1/tenant/manage/tasks/${id}/checklist`, { checklist, status });
    }

    async completeTask(id: string, payload: { keeper_notes?: string; proof_photos?: string[]; photos?: File[] }): Promise<any> {
        if (payload.photos && payload.photos.length > 0) {
            const formData = new FormData();
            if (payload.keeper_notes) formData.append('keeper_notes', payload.keeper_notes);
            payload.photos.forEach(photo => formData.append('photos[]', photo));
            return this.api.post(`api/v1/tenant/manage/tasks/${id}/complete`, formData);
        }
        return this.api.post(`api/v1/tenant/manage/tasks/${id}/complete`, payload);
    }

    // Room Cleaning Matrix & Auto-Generators
    async getRoomCleaningMatrix(intervalDays: number = 14): Promise<any> {
        return this.api.get(`api/v1/tenant/manage/tasks/room-matrix?interval_days=${intervalDays}`);
    }

    async generateRoomCleaningTasks(payload: {
        interval_days?: number;
        target_date?: string;
        assigned_to_user_id?: string;
        max_rooms?: number;
    }): Promise<any> {
        return this.api.post('api/v1/tenant/manage/tasks/generate-room-cleaning', payload);
    }

    async distributeCleaningCycle(payload: {
        cycle_days?: number;
        start_date?: string;
        assigned_to_user_id?: string;
        overwrite_future?: boolean;
        checklist?: any[];
    }): Promise<any> {
        return this.api.post('api/v1/tenant/manage/tasks/distribute-cycle', payload);
    }

    async generateTemplateTasks(targetDate?: string): Promise<any> {
        return this.api.post('api/v1/tenant/manage/tasks/generate-template-tasks', { target_date: targetDate });
    }

    // Templates
    async getTaskTemplates(): Promise<any> {
        return this.api.get('api/v1/tenant/manage/tasks/templates');
    }

    async createTaskTemplate(payload: any): Promise<any> {
        return this.api.post('api/v1/tenant/manage/tasks/templates', payload);
    }

    async updateTaskTemplate(id: string, payload: any): Promise<any> {
        return this.api.put(`api/v1/tenant/manage/tasks/templates/${id}`, payload);
    }

    async deleteTaskTemplate(id: string): Promise<any> {
        return this.api.delete('api/v1/tenant/manage/tasks/templates', id);
    }
}
